import { type ReactNode, useRef } from 'react';
import { type Location, useLocation, useSearchParams } from 'react-router-dom';
import Cookies from 'js-cookie';
import { type FrontRoyalRootScope, useCurrentUser, useOfflineModeManager, useRootScope } from 'FrontRoyalAngular';
import { type CurrentUser, getShouldBeRedirectedToApplication } from 'Users';
import { getShouldVisitBillingPage } from 'Users/userRedirectHelpers';
import { ROUTES as STUDENT_DASHBOARD_ROUTES } from 'StudentDashboard/constants';

type ForcedInitialRedirectRouteProps = {
    allowedWhenOnboardingIncomplete: boolean;
    children: ReactNode;
};

function shouldRedirectToConfirmProfile(currentUser: CurrentUser, location: Location) {
    return !currentUser.confirmedProfileInfo && !location.pathname.includes('/complete-registration');
}

function shouldRedirectToSignUpTarget(hasBeenRedirected: boolean) {
    return !hasBeenRedirected && Cookies.get('signup_target');
}

function shouldRedirectToApplication(
    currentUser: CurrentUser,
    hasBeenRedirected: boolean,
    searchParams: URLSearchParams,
    location: Location,
    allowedWhenOnboardingIncomplete: ForcedInitialRedirectRouteProps['allowedWhenOnboardingIncomplete'],
) {
    return (
        !hasBeenRedirected &&
        searchParams.get('try_lesson') !== 'true' &&
        location.pathname.includes(STUDENT_DASHBOARD_ROUTES.STUDENT_DASHBOARD.path) &&
        getShouldBeRedirectedToApplication(currentUser) &&
        !allowedWhenOnboardingIncomplete
    );
}

function shouldRedirectToBillingPage(
    currentUser: CurrentUser,
    hasCompletedFirstLoad: boolean,
    location: Location,
    $rootScope: FrontRoyalRootScope,
) {
    return (
        !hasCompletedFirstLoad &&
        location.pathname.includes($rootScope.homePath) &&
        getShouldVisitBillingPage(currentUser)
    );
}

// Take the current location and set it as the `target` parameter so we will
// redirect there after the user resolves whatever is currently pending
function addTargetToPath({ location, path }: { location: Location; path: string }) {
    const target = `${location.pathname}${location.search}`;
    return `${path}?target=${target}`;
}

/*
    In situations where the user needs to be pushed to a certain part of the app to
    resolve something about their state, this hook will push them there.
*/
export function useForcedRedirect({ allowedWhenOnboardingIncomplete }: ForcedInitialRedirectRouteProps): string | null {
    const currentUser = useCurrentUser();
    const $rootScope = useRootScope();
    const offlineModeManager = useOfflineModeManager();
    const location = useLocation();
    const [searchParams] = useSearchParams();
    const hasBeenRedirectedRef = useRef(false);
    const hasCompletedFirstLoadRef = useRef(false);

    if (!currentUser) return null;

    // Only allow these forced redirects if not in offline mode
    if (offlineModeManager.inOfflineMode) return null;

    if (shouldRedirectToConfirmProfile(currentUser, location)) {
        // retain target redirect support since this can happen to older users who might be reloading a path
        return addTargetToPath({ path: '/complete-registration', location });
    }

    // If we have an override for where to send the user after logging in
    if (shouldRedirectToSignUpTarget(hasBeenRedirectedRef.current)) {
        const redirectTarget = Cookies.get('signup_target')!;
        Cookies.remove('signup_target', {
            path: '/',
        });

        // prevent any more redirects for this user session
        hasBeenRedirectedRef.current = true;

        return redirectTarget;
    }

    // On first loading the app, send the user to the application if they
    // need to fill one out. Unless deeplinking beyond the dashboard.
    //  You can also add `try_lesson` as a query param to
    // the route to bypass this behavior (e.g. `/dashboard?try_lesson=true`).
    if (
        shouldRedirectToApplication(
            currentUser,
            hasBeenRedirectedRef.current,
            searchParams,
            location,
            allowedWhenOnboardingIncomplete,
        )
    ) {
        // Since hasBeenRedirectedRef is not stored, it will be
        // false on login/reload, preventing an infinite redirect loop.
        hasBeenRedirectedRef.current = true;

        return '/settings/application';
    }

    // On first loading the app, send the user to the cohort billing page if necessary
    if (shouldRedirectToBillingPage(currentUser, hasCompletedFirstLoadRef.current, location, $rootScope)) {
        // Might look odd at first glance, but the /settings/application_status route
        // serves as the cohort billing page.
        return '/settings/application_status';
    }

    hasCompletedFirstLoadRef.current = true;

    // Remove the try_lesson query param before we resolve to the desired route.
    // The user doesn't need to see it after we resolve the route and it mucks
    // up the URL unnecessarily.
    if (searchParams.get('try_lesson')) {
        searchParams.delete('try_lesson');
        return `${location.pathname}?${searchParams.toString()}`;
    }

    return null;
}
